/**
 * globalSliceSlider.js — общий вертикальный ползунок синхронного листания [6.0.1].
 * Как у станционного вьювера GE: тянешь ползунок — ВСЕ 2D-окна листаются
 * одновременно: и нативная стопка, и все три MPR-реконструкции.
 * У каждого окна свой диапазон — позиция нормируется 0..1.
 *
 * [6.0.1] Ортографические (MPR) окна не имеют getImageIds/setImageIdIndex,
 * поэтому для них ведётся собственный учёт позиции по событиям VOLUME_NEW_IMAGE
 * (индекс + всего срезов), а перемотка выполняется через scroll(delta) —
 * тот же примитив, которым листает колесо мыши (StackScrollTool).
 */
import { cornerstone, cornerstoneTools } from '../core/init.js';
import { getRenderingEngine, getViewportIds } from '../core/viewportState.js';
import { log } from '../utils/logger.js';

const MAX = 1000;
let _dragging = false;
let _bound = false;

/** Учёт позиции каждого окна: vpId -> { idx, total } (заполняется событиями) */
const _pos = new Map();

function _isVolumeVp(vp) {
  return typeof vp?.setImageIdIndex !== 'function';
}

function _numSlices(vp) {
  // 1. Современное API (есть у stack и volume в свежих Cornerstone)
  try { const n = vp.getNumberOfSlices?.(); if (Number.isFinite(n) && n > 0) return n; } catch (e) { /* нет метода */ }
  // 2. Стек: длина списка кадров
  try { const n = vp.getImageIds?.()?.length; if (n > 0) return n; } catch (e) { /* volume */ }
  // 3. Наш учёт по событиям
  const st = _pos.get(vp.id);
  if (st?.total > 1) return st.total;
  // 4. Утилита ядра для volume-вьюпортов
  try {
    const d = cornerstone.utilities?.getImageSliceDataForVolumeViewport?.(vp);
    if (d?.numberOfSlices > 0) return d.numberOfSlices;
  } catch (e) { /* недоступна */ }
  return 0;
}

function _curIndex(vp) {
  try { const i = vp.getCurrentImageIdIndex?.(); if (Number.isFinite(i)) return i; } catch (e) { /* volume */ }
  try { const i = vp.getSliceIndex?.(); if (Number.isFinite(i)) return i; } catch (e) { /* старое API */ }
  const st = _pos.get(vp.id);
  if (Number.isFinite(st?.idx)) return st.idx;
  try {
    const d = cornerstone.utilities?.getImageSliceDataForVolumeViewport?.(vp);
    if (Number.isFinite(d?.imageIndex)) return d.imageIndex;
  } catch (e) { /* недоступна */ }
  return 0;
}

function _jump(vp, idx, n) {
  try {
    // Стек — прямое позиционирование
    if (typeof vp.setImageIdIndex === 'function') { vp.setImageIdIndex(idx); vp.render?.(); return; }
    // Volume/MPR: scroll(delta) — примитив колеса мыши, работает всегда, где
    // работает StackScroll. Берём из tools, затем из core (место менялось между версиями).
    const cur = _curIndex(vp);
    const delta = idx - cur;
    if (!delta) return;
    const scroll = cornerstoneTools?.utilities?.scroll || cornerstone.utilities?.scroll;
    if (typeof scroll === 'function') {
      scroll(vp, { delta });
      const st = _pos.get(vp.id) || {};
      _pos.set(vp.id, { idx, total: st.total || n });
      return;
    }
    // Последний фолбэк — jumpToSlice, если есть
    const jts = cornerstoneTools?.utilities?.jumpToSlice;
    if (typeof jts === 'function') jts(vp.element, { imageIndex: idx });
  } catch (e) { log.debug('[Slider] jump', vp?.id, e.message); }
}

function _applyFraction(t) {
  const re = getRenderingEngine();
  if (!re) return;
  for (const vpId of (getViewportIds() || [])) {
    let vp;
    try { vp = re.getViewport(vpId); } catch (e) { continue; }
    if (!vp || String(vp.type || '').toLowerCase().includes('3d')) continue;
    const n = _numSlices(vp);
    if (n < 2) { log.debug('[Slider] пропуск', vpId, '— срезов:', n); continue; }
    const idx = Math.min(n - 1, Math.max(0, Math.round(t * (n - 1))));
    _jump(vp, idx, n);
  }
}

/**
 * [10.19.19] ГДЕ БЕГУНКУ БЫТЬ, А ГДЕ НЕ БЫТЬ. Одно правило, одно место.
 *
 * ПОВОД, дважды от Андрея Вячеславовича: «виден вертикальный бегунок, который
 * бесполезен в этом режиме, он не работает» — про объёмную проекцию, вызванную
 * кнопкой 3D, и «опять вертикальный бесполезный бегунок» — про одиночное окно
 * нативных срезов.
 *
 * Он был прав дважды. Бегунок появлялся при ПЕРВОМ ЖЕ событии среза
 * (`el.style.display = 'block'`) и после этого не скрывался никогда: смена
 * раскладки его не касалась. Листать объём он не умеет по устройству — код выше
 * пропускает 3D-окна, — то есть в объёмной проекции он висел и молчал.
 *
 * ПРАВИЛО. Бегунок нужен там, где он делает то, чего не делает колесо мыши:
 * листает НЕСКОЛЬКО окон разом. Считаем окна со срезами (не 3D, срезов ≥ 2):
 * два и больше — бегунок есть (MPR, MPR6, сравнение двух исследований); одно или
 * ни одного — бегунка нет (объёмная проекция, одиночная стопка: там колесо).
 *
 * Считается по САМИМ ОКНАМ, а не по названию раскладки: названий больше десятка,
 * и список из них разъехался бы с действительностью при первом же новом режиме.
 */
export function обновитьВидимостьБегунка() {
  const el = document.getElementById('global-slice-slider');
  if (!el) return { показан: false, причина: 'бегунка нет в разметке' };
  let сОкнами = 0, объёмных = 0;
  const re = getRenderingEngine();
  for (const vpId of (getViewportIds() || [])) {
    let vp;
    try { vp = re?.getViewport(vpId); } catch (e) { continue; }
    if (!vp) continue;
    if (String(vp.type || '').toLowerCase().includes('3d')) { объёмных++; continue; }
    if (_numSlices(vp) >= 2) сОкнами++;
  }
  const надо = сОкнами >= 2;
  el.style.display = надо ? 'block' : 'none';
  el.setAttribute('aria-hidden', надо ? 'false' : 'true');
  return { показан: надо, оконСоСрезами: сОкнами, объёмных };
}

/**
 * [11.0.7] Забыть позиции окон и пересчитать видимость — после очистки.
 *
 * ПОВОД. После «Очистить исследования» бегунок остался висеть поверх пустой
 * стартовой заставки. Правило видимости при этом верное — оно считает окна со
 * срезами, — но пересчитывается ОНО ТОЛЬКО ПО СОБЫТИЮ СРЕЗА (STACK_NEW_IMAGE,
 * VOLUME_NEW_IMAGE). При очистке срезы не приходят, приходить нечему — и
 * последнее выставленное `display:block` оставалось навсегда.
 *
 * Заодно забываем запомненные позиции: они относятся к окнам закрытого
 * исследования, и в следующем бегунок дёрнулся бы к чужому срезу.
 */
export function забытьПозицииБегунка() {
  _pos.clear();
  return обновитьВидимостьБегунка();
}

export function initGlobalSliceSlider() {
  // [7.0.55] Мобильная версия: ползунок листания не нужен (листание пальцем) — не инициализируем.
  if (window.matchMedia && window.matchMedia('(max-width: 768px)').matches) return;
  const el = document.getElementById('global-slice-slider');
  const container = document.getElementById('viewport-container');
  if (!el || !container || _bound) return;
  _bound = true;

  el.addEventListener('input', () => { _dragging = true; _applyFraction(el.value / MAX); });
  ['change', 'pointerup', 'pointercancel', 'touchend'].forEach(ev =>
    el.addEventListener(ev, () => { setTimeout(() => { _dragging = false; }, 50); }));

  // Учёт позиций всех окон + обратная связь ползунка.
  // Ползунок появляется при первом же событии среза; до загрузки данных скрыт.
  const upd = (vpId, idx, total) => {
    if (Number.isFinite(idx) && Number.isFinite(total) && total > 1) {
      _pos.set(vpId, { idx, total });
    }
    if (_dragging || !Number.isFinite(idx) || !Number.isFinite(total) || total < 2) return;
    el.value = String(Math.round((idx / (total - 1)) * MAX));
    // Показ решает ПРАВИЛО, а не факт события среза. Раньше здесь стояло
    // `el.style.display = 'block'` — и бегунок всплывал в любой раскладке,
    // где хоть раз пришёл срез, включая объёмную проекцию.
    обновитьВидимостьБегунка();
  };
  try {
    container.addEventListener(cornerstone.Enums.Events.STACK_NEW_IMAGE, (ev) => {
      const d = ev.detail || {};
      let total;
      try { total = getRenderingEngine()?.getViewport(d.viewportId)?.getImageIds?.()?.length; } catch (e) { total = 0; }
      upd(d.viewportId, d.imageIdIndex ?? 0, total);
    }, true);
    container.addEventListener(cornerstone.Enums.Events.VOLUME_NEW_IMAGE, (ev) => {
      const d = ev.detail || {};
      upd(d.viewportId, d.imageIndex ?? d.sliceIndex ?? 0, d.numberOfSlices);
    }, true);
  } catch (e) { log.debug('[Slider] bind:', e.message); }
}
